import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import mapboxgl from "mapbox-gl";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { mapboxgl_accessToken } from "@utils/baseValues";

mapboxgl.accessToken = mapboxgl_accessToken;

interface MapContextType {
  mapRef: React.RefObject<mapboxgl.Map | null>;
  mapContainerRef: React.RefObject<HTMLDivElement | null>;
  drawRef: React.RefObject<MapboxDraw | null>;
  mapLoaded: boolean;
}

const MapContext = createContext<MapContextType | undefined>(undefined);

export const MapProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const drawRef = useRef<MapboxDraw | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/streets-v11",
      center: [25.0, 0.0],
      zoom: 2,
    });
    mapRef.current.on("load", () => {
      setMapLoaded(true);
    });
  }, []);

  return (
    <MapContext.Provider
      value={{ mapRef, mapContainerRef, drawRef, mapLoaded }}
    >
      {children}
    </MapContext.Provider>
  );
};

export const useMapContext = (): MapContextType => {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error("useMapContext must be used within a MapProvider");
  }
  return context;
};
